import React, { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ContactUs from "src/Components/Modals/ContactUs";
import SeoHead from "src/Components/SeoHead";
import MonitoringMap from "src/Pages/Dashboard/MonitoringMap";
import { CommunityBoundary } from "src/Types/monitoring.types";
import {
  fetchCommunityPopulation,
  searchCommunityBoundaries,
} from "src/utils/apicalls";
import { trackEvent } from "src/utils/analytics";
import {
  breadcrumbSchema,
  faqPageSchema,
  organizationSchema,
  serviceSchema,
  webPageSchema,
} from "src/utils/seoSchema";
import { useLocale } from "src/i18n/LocaleProvider";
import { formatUsdAndLocal } from "src/i18n/currency";
import { homePath, pricingPath } from "src/i18n/paths";
import { getApiErrorMessage } from "src/utils/apiError";
import {
  calculateMunicipalQuote,
  CONTRACT_DISCOUNT_TIERS,
  CONTRACT_MONTH_STEP,
  MAXIMUM_CONTRACT_MONTHS,
  MINIMUM_CONTRACT_MONTHS,
} from "./pricing";

const Pricing: React.FC = () => {
  const { t } = useTranslation();
  const { locale, config } = useLocale();
  const usesCensus = config.usesCensusPricing;
  const termLabel = (months: number) =>
    t("pricing.term", { count: months / 12 });
  const populationFormat = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  });
  const discountSummary = CONTRACT_DISCOUNT_TIERS.map((tier) =>
    t("pricing.discountTier", {
      percent: tier.discountPercent,
      term: termLabel(tier.months),
    })
  ).join(", ");
  const sliderMarks = [12, 24, 36, 48, 60].map((value) => ({
    value,
    label: t("pricing.yearShort", { count: value / 12 }),
  }));
  const formatUsd = (amount: number) =>
    formatUsdAndLocal(amount, "USD", "en-US").usd;
  const formatDisplay = (amountUsd: number) => {
    if (config.currency === "USD") return formatUsd(amountUsd);
    const { local, usd } = formatUsdAndLocal(
      amountUsd,
      config.currency,
      config.htmlLang
    );
    return `${local} (≈ ${usd})`;
  };

  const pricingFaqs = [
    {
      question: t("pricing.faq.calculatedQuestion"),
      answer: usesCensus
        ? t("pricing.faq.calculatedAnswerUs")
        : t("pricing.faq.calculatedAnswerIntl"),
    },
    {
      question: t("pricing.faq.populationQuestion"),
      answer: usesCensus
        ? t("pricing.faq.populationAnswerUs")
        : t("pricing.faq.populationAnswerIntl"),
    },
    {
      question: t("pricing.faq.termQuestion"),
      answer: t("pricing.faq.termAnswer", { discounts: discountSummary }),
    },
    {
      question: t("pricing.faq.bindingQuestion"),
      answer: t("pricing.faqBinding"),
    },
  ];

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CommunityBoundary[]>([]);
  const [selected, setSelected] = useState<CommunityBoundary | null>(null);
  const [contractMonths, setContractMonths] = useState(
    MINIMUM_CONTRACT_MONTHS
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [populationLoading, setPopulationLoading] = useState(false);
  const [populationError, setPopulationError] = useState("");
  const [populationInput, setPopulationInput] = useState("");
  const [populationSource, setPopulationSource] = useState<
    "census" | "manual" | null
  >(null);
  const lastQuoteViewedKeyRef = useRef<string | null>(null);

  const population = useMemo(() => {
    const parsed = Number(populationInput);
    if (!Number.isFinite(parsed) || parsed <= 0 || populationInput.trim() === "") {
      return null;
    }
    return parsed;
  }, [populationInput]);

  const quote = useMemo(
    () =>
      selected && population != null
        ? calculateMunicipalQuote(population, contractMonths)
        : null,
    [contractMonths, population, selected]
  );

  useEffect(() => {
    if (!selected) return;

    let cancelled = false;
    const loadPopulation = async () => {
      setPopulationLoading(true);
      setPopulationError("");
      setPopulationInput("");
      setPopulationSource(null);

      if (!usesCensus) {
        setPopulationError(t("pricing.censusFallback"));
        setPopulationSource("manual");
        setPopulationLoading(false);
        return;
      }

      try {
        const result = await fetchCommunityPopulation(selected.center);
        if (cancelled) return;
        setPopulationInput(String(Math.round(result.population)));
        setPopulationSource("census");
        trackEvent("pricing_population_lookup", {
          community_name: selected.name,
          success: true,
          population_source: "census",
        });
      } catch (requestError: any) {
        if (cancelled) return;
        setPopulationError(
          getApiErrorMessage(requestError, t, "pricing.populationLookupFailed")
        );
        setPopulationSource("manual");
        trackEvent("pricing_population_lookup", {
          community_name: selected.name,
          success: false,
          population_source: "manual",
        });
      } finally {
        if (!cancelled) setPopulationLoading(false);
      }
    };

    void loadPopulation();
    return () => {
      cancelled = true;
    };
  }, [selected, usesCensus, t]);

  useEffect(() => {
    if (!selected || !quote) return;
    const key = selected.name;
    if (lastQuoteViewedKeyRef.current === key) return;
    lastQuoteViewedKeyRef.current = key;
    trackEvent("pricing_quote_viewed", {
      community_name: selected.name,
      annual_estimate: quote.annualPrice,
      contract_months: contractMonths,
      population: quote.population,
      currency: config.currency,
    });
  }, [selected, quote, contractMonths, config.currency]);

  const handleSearch = async (event: FormEvent) => {
    event.preventDefault();
    const value = query.trim();
    if (!value) {
      setError(t("pricing.searchRequired"));
      return;
    }
    setLoading(true);
    setError("");
    setSelected(null);
    lastQuoteViewedKeyRef.current = null;
    setPopulationInput("");
    setPopulationSource(null);
    setPopulationError("");
    try {
      const communities = await searchCommunityBoundaries(value);
      setResults(communities);
      trackEvent("pricing_search", {
        search_term: value,
        result_count: communities.length,
        success: true,
        locale,
      });
      if (communities.length === 0) {
        setError(t("pricing.noBoundary"));
      }
    } catch (requestError: any) {
      setResults([]);
      trackEvent("pricing_search", {
        search_term: value,
        result_count: 0,
        success: false,
        locale,
      });
      setError(getApiErrorMessage(requestError, t, "pricing.searchUnavailable"));
    } finally {
      setLoading(false);
    }
  };

  const handleCommunitySelect = (community: CommunityBoundary) => {
    lastQuoteViewedKeyRef.current = null;
    setSelected(community);
    trackEvent("pricing_community_select", {
      community_name: community.name,
      area_sq_mi: Number(community.areaSqMiles.toFixed(1)),
    });
  };

  const trackQuoteCtaClick = (ctaLabel: string) => {
    trackEvent("pricing_quote_cta_click", {
      cta_label: ctaLabel,
      community_name: selected?.name,
      has_quote: Boolean(quote),
    });
  };

  const localNote =
    quote && config.currency !== "USD"
      ? (() => {
          const converted = formatUsdAndLocal(
            quote.contractTotal,
            config.currency,
            config.htmlLang
          );
          return t("pricing.localCurrencyNote", {
            local: converted.local,
            usd: converted.usd,
            currency: config.currency,
          });
        })()
      : null;

  const quoteMessage =
    selected && quote
      ? [
          t("pricing.quoteMessage", {
            name: selected.name,
            population: populationFormat.format(quote.population),
            term: termLabel(contractMonths),
            total: formatUsd(quote.contractTotal),
          }),
          populationSource === "census" ? t("pricing.quoteMessageCensus") : "",
          quote.contractDiscountPercent > 0
            ? t("pricing.quoteMessageDiscount", {
                percent: quote.contractDiscountPercent,
              })
            : "",
          config.currency !== "USD"
            ? t("pricing.quoteMessageCurrency", { currency: config.currency })
            : "",
          t("pricing.quoteMessageInvoice", { currency: config.currency }),
        ]
          .filter(Boolean)
          .join(" ")
      : t("pricing.quoteMessageGeneric");

  const pagePath = pricingPath(locale);
  const pricingPageTitle = t("pricing.seoTitle");
  const pricingPageDescription = usesCensus
    ? t("pricing.seoDescriptionUs")
    : t("pricing.seoDescription");

  return (
    <>
      <SeoHead
        title={pricingPageTitle}
        description={pricingPageDescription}
        canonical={pagePath}
        keywords={t("pricing.seoKeywords")}
        jsonLd={[
          organizationSchema(),
          webPageSchema({
            name: pricingPageTitle,
            description: pricingPageDescription,
            url: pagePath,
          }),
          serviceSchema({
            name: t("pricing.schemaName"),
            description: pricingPageDescription,
            audience: t("pricing.schemaAudience"),
            url: pagePath,
            areaServedCountry: config.countryName,
          }),
          faqPageSchema(pricingFaqs),
          breadcrumbSchema([
            { name: t("nav.home"), url: homePath(locale) },
            { name: t("nav.pricing"), url: pagePath },
          ]),
        ]}
      />
      <Box
        component="section"
        sx={{
          width: "100%",
          px: { xs: 2, sm: 3, lg: 4 },
          py: { xs: 6, md: 9 },
          background:
            "radial-gradient(circle at 50% 0%, rgba(124,58,237,0.2), transparent 42%), #0b0f17",
        }}
      >
        <Stack spacing={5} sx={{ width: "100%", maxWidth: 1180, mx: "auto" }}>
          <Stack spacing={2} textAlign="center" alignItems="center">
            <Chip label={t("pricing.chip")} color="primary" />
            <Typography
              component="h1"
              sx={{
                maxWidth: 800,
                color: "#fff",
                fontWeight: 800,
                fontSize: { xs: "2.25rem", md: "3.5rem" },
                lineHeight: 1.08,
              }}
            >
              {t("pricing.title")}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ maxWidth: 720, fontSize: { xs: "1rem", md: "1.125rem" } }}
            >
              {usesCensus
                ? t("pricing.subtitleUs")
                : t("pricing.subtitleIntl", { currency: config.currency })}
            </Typography>
          </Stack>

          <Card sx={{ p: { xs: 2, md: 3 } }}>
            <Box component="form" onSubmit={handleSearch}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  fullWidth
                  label={t("pricing.searchLabel")}
                  placeholder={t("pricing.searchPlaceholder")}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  inputProps={{ maxLength: 300 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    loading ? <CircularProgress size={16} /> : <SearchIcon />
                  }
                  disabled={loading}
                  sx={{
                    minWidth: 176,
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  {loading ? t("pricing.searching") : t("pricing.findBoundary")}
                </Button>
              </Stack>
            </Box>

            {error && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {results.length > 0 && !selected && (
              <Stack spacing={1.25} mt={3}>
                <Typography fontWeight={700}>
                  {t("pricing.selectJurisdiction")}
                </Typography>
                {results.map((community) => (
                  <Button
                    key={community.providerId || community.name}
                    variant="outlined"
                    onClick={() => handleCommunitySelect(community)}
                    sx={{
                      justifyContent: "space-between",
                      textAlign: "left",
                      gap: 2,
                    }}
                  >
                    <Box component="span">{community.name}</Box>
                    <Box component="span" sx={{ flexShrink: 0 }}>
                      {t("pricing.areaSqMi", { area: community.areaSqMiles.toFixed(1) })}
                    </Box>
                  </Button>
                ))}
              </Stack>
            )}
          </Card>

          {selected && (
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={3}
              alignItems="stretch"
            >
              <Card sx={{ p: { xs: 2.5, md: 3.5 }, flex: 1 }}>
                <Stack spacing={3}>
                  <Box>
                    <Typography color="primary.light" fontWeight={700}>
                      {t("pricing.selectedJurisdiction")}
                    </Typography>
                    <Typography variant="h5" mt={0.5}>
                      {selected.name}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      height: 240,
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <MonitoringMap
                      mode="COMMUNITY"
                      community={selected}
                      addresses={[]}
                      alerts={[]}
                      selectedAlertId={null}
                      onSelectAlert={() => undefined}
                      fullBleed
                    />
                  </Box>

                  <Divider />

                  <Box>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="baseline"
                      spacing={2}
                    >
                      <Typography fontWeight={700}>{t("pricing.population")}</Typography>
                      {populationLoading && <CircularProgress size={16} />}
                    </Stack>
                    <TextField
                      fullWidth
                      type="number"
                      label={t("pricing.residents")}
                      value={populationInput}
                      onChange={(event) => {
                        setPopulationInput(event.target.value);
                        setPopulationSource("manual");
                      }}
                      disabled={populationLoading}
                      inputProps={{ min: 1, step: 1 }}
                      sx={{ mt: 1.5 }}
                      helperText={
                        populationSource === "census"
                          ? t("pricing.populationHelpCensus")
                          : t("pricing.populationHelpManual")
                      }
                    />
                    {populationError && (
                      <Alert severity="info" sx={{ mt: 1.5 }}>
                        {populationError}
                      </Alert>
                    )}
                  </Box>

                  <Divider />

                  <Box>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="baseline"
                    >
                      <Typography fontWeight={700}>{t("pricing.contractTerm")}</Typography>
                      <Typography color="primary.light" fontWeight={800}>
                        {termLabel(contractMonths)}
                      </Typography>
                    </Stack>
                    <Slider
                      aria-label={t("pricing.contractTerm")}
                      value={contractMonths}
                      min={MINIMUM_CONTRACT_MONTHS}
                      max={MAXIMUM_CONTRACT_MONTHS}
                      step={CONTRACT_MONTH_STEP}
                      marks={sliderMarks}
                      valueLabelDisplay="auto"
                      valueLabelFormat={termLabel}
                      onChange={(_event, value) =>
                        setContractMonths(value as number)
                      }
                      sx={{ mt: 3 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {t("pricing.termHelp", { discounts: discountSummary })}
                    </Typography>
                  </Box>
                </Stack>
              </Card>

              <Card
                sx={{
                  p: { xs: 2.5, md: 3.5 },
                  width: { xs: "100%", md: 380 },
                  borderColor: "rgba(139, 92, 246, 0.7)",
                }}
              >
                <Stack spacing={3}>
                  <Box>
                    <Typography color="text.secondary">
                      {t("pricing.estimatedContract", { term: termLabel(contractMonths) })}
                    </Typography>
                    {quote && quote.contractDiscountPercent > 0 && (
                      <Typography
                        color="text.secondary"
                        sx={{
                          textDecoration: "line-through",
                          fontSize: "0.95rem",
                          mt: 0.5,
                        }}
                      >
                        {formatDisplay(quote.contractSubtotal)}
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        color: "#fff",
                        fontSize: { xs: "2.5rem", md: "3rem" },
                        fontWeight: 800,
                        lineHeight: 1.1,
                      }}
                    >
                      {quote ? formatDisplay(quote.contractTotal) : "—"}
                    </Typography>
                    {quote && quote.contractDiscountPercent > 0 ? (
                      <Typography color="primary.light" fontWeight={700}>
                        {t("pricing.discount", { percent: quote.contractDiscountPercent })}
                      </Typography>
                    ) : (
                      <Typography color="text.secondary">
                        {t("pricing.minimumTerm")}
                      </Typography>
                    )}
                    {quote && (
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        {t("pricing.includes")}
                      </Typography>
                    )}
                    {localNote && (
                      <Typography variant="body2" color="primary.light" mt={1}>
                        {localNote}
                      </Typography>
                    )}
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {usesCensus
                        ? t("pricing.estimateNoteUs")
                        : t("pricing.estimateNoteIntl")}
                    </Alert>
                  </Box>
                  <ContactUs
                    buttonLabel={t("pricing.requestFormalQuote")}
                    buttonLabelMobile={t("pricing.requestFormalQuote")}
                    defaultMessage={quoteMessage}
                    dialogTitle={t("pricing.formalQuoteTitle")}
                    dialogSubtitle={t("pricing.formalQuoteSubtitle")}
                    analyticsContext={{
                      source: "pricing_formal_quote",
                      community_name: selected.name,
                      has_quote: true,
                    }}
                    onDialogOpen={() =>
                      trackQuoteCtaClick("Request formal quote")
                    }
                  />
                  <Button
                    variant="text"
                    onClick={() => {
                      setSelected(null);
                      setResults([]);
                      lastQuoteViewedKeyRef.current = null;
                      setPopulationInput("");
                      setPopulationSource(null);
                      setPopulationError("");
                    }}
                  >
                    {t("pricing.searchAnother")}
                  </Button>
                </Stack>
              </Card>
            </Stack>
          )}

          {!selected && (
            <Stack alignItems="center" spacing={1.5}>
              <Typography color="text.secondary">
                {t("pricing.cannotFind")}
              </Typography>
              <ContactUs
                buttonLabel={t("pricing.requestManualQuote")}
                buttonLabelMobile={t("pricing.requestManualQuote")}
                defaultMessage={quoteMessage}
                dialogTitle={t("pricing.manualQuoteTitle")}
                analyticsContext={{
                  source: "pricing_manual_quote",
                  has_quote: false,
                }}
                onDialogOpen={() =>
                  trackQuoteCtaClick("Request a manual quote")
                }
              />
            </Stack>
          )}
        </Stack>
      </Box>
    </>
  );
};

export default Pricing;
